import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Tooltip } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as GetAppIcon,
  Replay as ReplayIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import type { DocumentActionButtonsProps } from '../types';
import { isLinkOnlyDocument } from '../../../../hooks/documents/useDocumentTable';

/**
 * DocumentActionButtons - Action buttons for a document
 *
 * Pure presentational component that displays action buttons:
 * - View: Always visible (opens the source address for SAP and URL documents)
 * - Download: Only for documents with a stored file (not SAP or URL)
 * - Edit: Only if user has edit permission
 * - Delete: Only if user has delete permission
 *
 * @param doc - The document to display actions for
 * @param canEdit - Whether user can edit this document
 * @param canDelete - Whether user can delete this document
 * @param onView - Callback when view button is clicked
 * @param onDownload - Callback when download button is clicked
 * @param onEdit - Callback when edit button is clicked
 * @param onDelete - Callback when delete button is clicked
 */
export const DocumentActionButtons: React.FC<DocumentActionButtonsProps> = ({
  doc,
  canEdit,
  canDelete,
  onView,
  onDownload,
  onEdit,
  onDelete,
  onReprocess,
  status,
  retryExhausted = false,
  retryExhaustedHint
}) => {
  const { t } = useTranslation();
  // SAP and URL documents are references to an address; no file is stored for
  // them, so only the view button (which opens that address) makes sense.
  const isLinkOnly = isLinkOnlyDocument(doc);

  return (
    <>
      {/* View in Browser Button */}
      <IconButton
        color="primary"
        onClick={() => onView(doc)}
        title={t('documents.table.view')}
        aria-label={t('documents.table.view')}
        size="small"
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>

      {/* Download Button - only for documents with a stored file */}
      {!isLinkOnly && (
        <IconButton
          color="primary"
          onClick={() => onDownload(doc)}
          title={t('documents.table.download')}
          aria-label={t('documents.table.download')}
          size="small"
        >
          <GetAppIcon fontSize="small" />
        </IconButton>
      )}

      {/* Edit Button - only if user has permission */}
      {canEdit && (
        <IconButton
          color="primary"
          onClick={() => onEdit(doc)}
          title={t('documents.table.edit')}
          aria-label={t('documents.table.edit')}
          size="small"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}

      {/* Re-index Button - failed documents only, owner or admin; disabled at the retry cap */}
      {canEdit && onReprocess && status === 'failed' && (
        <Tooltip arrow title={retryExhausted ? (retryExhaustedHint ?? '') : t('documents.table.reindexHint')}>
          <span>
            <IconButton
              color="primary"
              onClick={() => onReprocess(doc)}
              aria-label={t('documents.table.reindex')}
              size="small"
              disabled={retryExhausted}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Delete Button - only if user has permission */}
      {canDelete && (
        <IconButton
          color="error"
          onClick={() => onDelete(doc.document_id || '')}
          title={t('documents.table.delete')}
          aria-label={t('documents.table.delete')}
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </>
  );
};

DocumentActionButtons.displayName = 'DocumentActionButtons';
